import { useEffect, useRef, createElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { LogIn, LogOut, MessageSquare } from 'lucide-react';
import { getCurrentTenantId } from '@/lib/tenant';
import { API_URL } from '@/lib/api-url';
import { isSoundMuted } from '@/lib/notification-sound';

function playCheckInSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const playBeep = (freq: number, start: number, duration: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + duration);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + duration);
    };
    playBeep(660, 0, 0.15);
    playBeep(880, 0.2, 0.15);
    playBeep(1046, 0.4, 0.25);
  } catch {
    // Browser may block audio without user interaction
  }
}

function playCheckOutSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const playBeep = (freq: number, start: number, duration: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + duration);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + duration);
    };
    playBeep(1046, 0, 0.15);
    playBeep(880, 0.2, 0.15);
    playBeep(660, 0.4, 0.25);
  } catch {
    // Browser may block audio without user interaction
  }
}

export function useServerEvents() {
  const queryClient = useQueryClient();
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const tenantId = getCurrentTenantId();
    const params = new URLSearchParams({ token });
    if (tenantId) params.set('tenantId', tenantId);

    const url = `${API_URL}/events?${params.toString()}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (e) => {
      try {
        const { event, data } = JSON.parse(e.data);

        if (event === 'order.new') {
          queryClient.invalidateQueries({ queryKey: ['store', 'orders'] });
          toast('New order received', {
            description: `${data.itemCount} item(s) — ${data.totalAmount?.toLocaleString()}`,
          });
        }

        if (event === 'order.updated') {
          queryClient.invalidateQueries({ queryKey: ['store', 'orders'] });
        }

        if (event === 'checkin') {
          queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
          if (!isSoundMuted()) playCheckInSound();
          toast(`${data.userName} checked in`, {
            description: `${data.gymName} · ${new Date(data.eventTime).toLocaleTimeString()}`,
            icon: createElement(LogIn, { className: 'text-green-500', size: 18 }),
          });
        }

        if (event === 'checkout') {
          queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
          if (!isSoundMuted()) playCheckOutSound();
          toast(`${data.userName} checked out`, {
            description: `${data.gymName} · ${new Date(data.eventTime).toLocaleTimeString()}`,
            icon: createElement(LogOut, { className: 'text-orange-500', size: 18 }),
          });
        }

        if (event === 'chat.message') {
          queryClient.invalidateQueries({ queryKey: ['chat', 'messages'] });
          queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] });
          queryClient.invalidateQueries({ queryKey: ['chat', 'my-conversation'] });
          queryClient.invalidateQueries({ queryKey: ['chat', 'unread-count'] });
          if (data.userName) {
            toast(`New message from ${data.userName}`, {
              description: data.content?.substring(0, 80) || '',
              icon: createElement(MessageSquare, { className: 'text-blue-500', size: 18 }),
            });
          }
        }

        if (event === 'chat.read') {
          queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] });
          queryClient.invalidateQueries({ queryKey: ['chat', 'unread-count'] });
        }
      } catch {
        // ignore malformed events / keepalive pings
      }
    };

    es.onerror = () => {
      // EventSource auto-reconnects; no manual action needed
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [queryClient]);
}
